import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Alert, DropdownButton, MenuItem } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Toggle from 'react-toggle';
import type { VoidFunctionComponent } from 'react';

import { UserOrgRoleManagement, UserTeamAndRoleManagement } from '../../../../../shared/organizations/types';
import { isFreeTier } from '../../../../../shared/subscriptions';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { useParams, useRouter } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';
import { useHasPermissionForTeamMembers } from '../../../../helpers/permissions';
import { orgRolesList, roleLabel } from '../../../../helpers/roles';
import { useOnMount } from '../../../../hooks';
import { useRoles } from '../../../../hooks/api';
import UsersModule from '../../../../redux/users';
import { useHasPermission } from '../../../../shared/permissions';
import { hasError } from '../../../../utils';
import { Pill } from '../../../generic';
import Flex from '../../../generic/Flex';
import { FormGroup, FormLabel, LabeledTextInput } from '../../../generic/Forms';
import Loader from '../../../generic/Loader';
import Text from '../../../generic/Text';
import Tooltip from '../../../generic/Tooltip';
import Header from '../../../shared/settings/SettingsPageHeader';
import TrustRelationshipsTable from '../../integrations/oidcProviders/TrustRelationshipsTable';
import { useCanViewTrustRelationships } from '../../shared/permissions';
import useUserRoleManagement from '../hooks/useUserRoleManagement';
import TeamRoleSelector from '../teams/TeamRoleSelector';
import type { SetUserTeamRolesRequest } from '../../../../../typings/api';
import type { ReduxStore } from '../../../../redux/store';

import styles from './UsersTable.module.less';

const EditUserSettings: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { id: userId } = useParams<{ id: number }>();
  const hasPermission = useHasPermission();
  const hasPermissionForTeamMembers = useHasPermissionForTeamMembers();
  const user = useSelector((store: ReduxStore) => UsersModule.selectors.getUser(store, userId));
  const status = useSelector(UsersModule.selectors.getStatus);
  const { data: rolesData = [] } = useRoles();
  const roles = _.keyBy(rolesData, (role) => role.id);
  const { data: userRoleManagement } = useUserRoleManagement();
  const canViewTrustRelationships = useCanViewTrustRelationships();
  const isOrgFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  const [selectedRoleId, setSelectedRoleId] = useState(user?.userRole?.roleId || null);
  const [isEnabled, setIsEnabled] = useState(user?.enabled ?? true);
  const [userTeamRoles, setUserTeamRoles] = useState(user?.teamUsers || []);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const updateRole = (roleId: number | null) =>
    dispatch(UsersModule.actions.updatePermission(userId, roleId));

  const updateTeamRoles = (newRoles: SetUserTeamRolesRequest) =>
    dispatch(UsersModule.actions.setTeamRoles(userId, newRoles));

  const updateEnabled = (enabled: boolean) =>
    dispatch(UsersModule.actions.updateEnabled(userId, enabled));

  useOnMount(() => {
    //  This will already be fetched if they navigated from the users list page,
    //  but if they refresh the page we need to ensure that the user is in the Redux store.
    dispatch(UsersModule.actions.fetchOne(userId));
  });

  useEffect(() => {
    setSelectedRoleId(user?.userRole?.roleId || null);
    setUserTeamRoles(user?.teamUsers || []);
    setIsEnabled(user?.enabled ?? true);
  }, [user]);

  const heading = useMemo(() => (
    <span className={styles.userHeading}>
      <span>Edit User &ldquo;{user?.username ?? 'Unknown User'}&rdquo;</span>
      {user?.isServiceAccount && (
        <Pill color="blue">Service Account</Pill>
      )}
    </span>
  ), [user?.username, user?.isServiceAccount]);

  if (status.loading || !user) {
    return (
      <div style={{ minHeight: '400px' }}>
        <Flex>
          <Loader />
        </Flex>
      </div>
    );
  }

  const isUserRoleIDPManaged = userRoleManagement?.organization === UserOrgRoleManagement.IDP;
  const isTeamRoleIDPManaged = userRoleManagement?.teams === UserTeamAndRoleManagement.IDP;
  const canManageRoles = hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });
  const canEditOrgRole = canManageRoles && !isUserRoleIDPManaged &&
    window.user?.id !== Number(userId); // Can't edit own role; userId comes from params, which stringifies them.

  const submitUserUpdate = async () => {
    setFeedbackMessage(null);

    const promises: Array<ReturnType<typeof dispatch>> = [];

    if (canEditOrgRole && selectedRoleId !== (user.userRole?.roleId || null)) {
      promises.push(updateRole(selectedRoleId));
    }

    const teamRoleChanges: SetUserTeamRolesRequest = [];
    userTeamRoles.forEach(teamUser => {
      if (hasPermissionForTeamMembers(Action.Edit, teamUser.team.id)) {
        teamRoleChanges.push({ teamId: teamUser.team.id, roleId: teamUser.roleId });
      }
    });

    if (teamRoleChanges.length) {
      promises.push(updateTeamRoles(teamRoleChanges));
    }

    try {
      await Promise.all(promises);

      // Update the enabled status separately so that we don't clobber the "errors" array in Redux.
      if (isEnabled !== user.enabled) {
        await updateEnabled(isEnabled);
      }
    } catch (e) {
      // Errors are handled by Redux.
    }

    setHasUnsavedChanges(false);
    setFeedbackMessage('User settings successfully updated');
  };

  const cancelUserUpdate = () => {
    router.push('/account/settings/organization/users');
  };

  const updateUserTeamRoles = (index: number, roleId: number) => {
    // we create a copy of our teamUsers array
    const newRoles = [...userTeamRoles];

    // // work on the matching team selected in the drop down
    const currTeam = newRoles[index];
    if (currTeam) {
      currTeam.roleId = roleId;
    }

    setUserTeamRoles(newRoles); // for ux purposes, we update teamUsers with the newRoles array
  };

  const successAlert = () => {
    if (feedbackMessage) {
      return (
        <Alert
          bsStyle="success"
          dismissAfter={2000}
          onDismiss={() => {
            setFeedbackMessage(null);
          }}>
          {feedbackMessage}
        </Alert>
      );
    }

    return null;
  };

  return (
    <>
      {/* We don't display an error alert here because AccountSettingsPage does that. */}
      <div className="margin-top-sm margin-bottom-sm">
        {!hasError(status) && successAlert()}
      </div>
      <Header heading={heading}>
        <Header.Button
          type="secondary"
          onClick={cancelUserUpdate}
        >
          Cancel
        </Header.Button>
        <Header.SaveButton
          onClick={submitUserUpdate}
          isDisabled={!hasUnsavedChanges}
        />
      </Header>
      <Text.H2>
        User Details
      </Text.H2>
      <div className="width-half">
        <FormGroup spacing="lg">
          <LabeledTextInput
            labelVariant="legacy"
            label="Username"
            value={user.username || ''}
            placeholder="Username"
            isDisabled
            isRequired
          />
          <LabeledTextInput
            labelVariant="legacy"
            label="User Email"
            value={user.email || ''}
            placeholder="User Email"
            isDisabled
            isRequired
          />
        </FormGroup>
      </div>
      <div className="user-settings__status-toggle">
        <Tooltip
          id="user-settings-status-toggle"
          showTooltip={!canEditOrgRole}
          tooltip="You are not permitted to enable or disable this user."
        >
          <div style={{ display: 'flex' }}>
            <Toggle
              id="user-settings-status-toggle"
              checked={isEnabled}
              onChange={(e) => {
                setIsEnabled(e.target.checked);
                setHasUnsavedChanges(true);
              }}
              icons={false}
              disabled={!canEditOrgRole} />
          </div>
        </Tooltip>
        <span className="padding-left-sm">
          <FormLabel
            label={isEnabled ? 'Enabled' : 'Disabled'}
            htmlFor="user-settings-status-toggle"
            isInline
          />
        </span>
      </div>
      <hr />
      <div className="width-half">
        <FormGroup spacing="lg">
          <>
            <Text.H2>
              Role
            </Text.H2>
            <FormLabel
              label="Inside the organization"
              htmlFor="user-org-role-dropdown"
              requiredText="(not required)"
            />
            <DropdownButton
              id="user-org-role-dropdown"
              title={roleLabel(roles, selectedRoleId)}
              onSelect={(roleId: unknown) => {
                if (roleId === null || _.isNumber(roleId)) {
                  setSelectedRoleId(roleId);
                  setHasUnsavedChanges(true);
                }
              }}
              bsStyle="default"
              disabled={!canEditOrgRole}>
              {orgRolesList(roles).map(role => (
                <MenuItem
                  key={role.id || 0}
                  eventKey={role.id}
                  active={selectedRoleId === role.id}
                  name="roleId">
                  {role.name}
                </MenuItem>
              ))}
            </DropdownButton>
          </>
          <>
            <Text.H2>
              Team
            </Text.H2>
            <Text.Paragraph>
              This user&apos;s roles in their teams.
            </Text.Paragraph>
            <FormGroup spacing="lg">
              {userTeamRoles.map((team, index) => (
                <Flex
                  key={team.team.id}
                  justify="flex-start"
                >
                  <FormLabel
                    htmlFor={`role-select-${team.team.id}`}
                    isInline
                    label={(
                      <span className="user-settings__team-name">
                        {team.team.name}
                      </span>
                    )}
                  />
                  <TeamRoleSelector
                    userId={user.id}
                    roleId={team.roleId}
                    teamId={team.team.id}
                    isDisabled={isTeamRoleIDPManaged}
                    handleChangeRole={(_userId: number, roleId: number) => {
                      updateUserTeamRoles(index, roleId);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </Flex>
              ))}
            </FormGroup>
          </>
        </FormGroup>
      </div>
      {canViewTrustRelationships && !isOrgFreeTier && user.isServiceAccount && (
        <>
          <hr />
          <Text.H2>
            Trust Relationships
          </Text.H2>
          <TrustRelationshipsTable userId={user.id} />
        </>
      )}
    </>
  );
};

export default EditUserSettings;
